/**
 * TierStyleProvider Component
 *
 * Handles tier-specific styling for web platform:
 * - Sets data-tier attribute on HTML element for CSS variables
 * - Triggers tier upgrade animation when tier changes (Phase 4)
 * - Persists tier to localStorage for FOUC prevention
 *
 * Use useTier() hook from @/hooks/useTier to access tier data.
 *
 * This component handles:
 * - Web: Sets data-tier attribute on HTML element, persists to localStorage
 * - Web: Triggers tier upgrade animation when tier changes (Phase 4)
 * - Native: Placeholder for font loading (to be implemented when fonts are available)
 *
 * Font Loading Strategy:
 * - Layer 1: No additional fonts (Inter fallback, Courier New system font)
 * - Layer 2: Neue Haas Grotesk Display declared in global.css
 * - Layer 3: Miller Display declared in global.css
 *
 * Web: Fonts declared in global.css with @font-face + font-display
 * Native: Fonts will be loaded via expo-font (when font files are added)
 *
 * Phase 4 Enhancement: Tier Upgrade Animation
 * - Detects tier changes (Layer 1 → 2 → 3)
 * - Applies subtle 300ms "re-skin" animation to document.body
 * - Tracks analytics event: Tier_Upgraded_Visual
 * - Respects prefers-reduced-motion accessibility preference
 */
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useTier } from '../hooks/useTier';
import { captureEvent } from '../lib/analytics';

export function TierStyleProvider({ children }: { children: React.ReactNode }) {
  // Use visualLayer for CSS data-tier attribute (matches 'layer1' | 'layer2' | 'layer3')
  const { visualLayer } = useTier();
  const previousTierRef = useRef<string | null>(null);

  // Web platform: Set data-tier attribute, persist to localStorage, trigger upgrade animation
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof document === 'undefined') return;

    const previousTier = previousTierRef.current;
    const isTierUpgrade = previousTier !== null && previousTier !== visualLayer;

    // Set data-tier attribute on HTML element for CSS variables
    document.documentElement.setAttribute('data-tier', visualLayer);

    // Persist tier to localStorage for FOUC prevention
    // This will be read by the inline script in web/index.html before React hydration
    try {
      localStorage.setItem('userTier', visualLayer);
    } catch (e) {
      // Ignore localStorage errors (e.g., in incognito mode)
      console.warn('Failed to persist tier to localStorage:', e);
    }

    // Phase 4: Tier Upgrade Animation
    // Trigger visual "re-skin" animation when tier changes (Layer 1 → 2 → 3)
    if (isTierUpgrade) {
      // Check if user prefers reduced motion
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!prefersReducedMotion) {
        // Apply tier-upgrade-animation class to document.body
        document.body.classList.add('tier-upgrade-animation');

        // Remove animation class after animation completes (300ms + 50ms buffer)
        setTimeout(() => {
          document.body.classList.remove('tier-upgrade-animation');
        }, 350);
      }

      // Track tier upgrade in PostHog analytics
      captureEvent('Tier_Upgraded_Visual', {
        from_tier: previousTier,
        to_tier: visualLayer,
        timestamp: new Date().toISOString(),
      });

      console.log(`[TierStyleProvider] Tier upgraded: ${previousTier} → ${visualLayer}`);
    }

    // Update ref for next render
    previousTierRef.current = visualLayer;
  }, [visualLayer]);

  // React Native platform: Font loading will be added when font files are available
  // For now, use system fonts (Inter, Courier New)
  // TODO: Add expo-font integration once premium fonts are licensed

  return <>{children}</>;
}
